import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  MacroAdjustmentCombinations,
  MacroAdjustmentStep,
  MacroAdjustmentWizardFormikValues,
} from "features/macro-tool/components/MacroAdjustmentWizard/MacroAdjustmentTypes";
import { CustomIcon, Icons } from "components/common";
import { useGetAllScenariosMacroScenarioIdAdjustmentsGet } from "orval/generated/endpoint";
import {
  CheckConflictingAdjustment,
  CheckConflictingEfAdjustment,
} from "../HelperFunctions";

export type MacroAdjustmentsWizardHeaderAndStepperProps = {
  handleNavigateToAdjustmentsPage: (
    formikProps: FormikProps<MacroAdjustmentWizardFormikValues>,
  ) => void;
  formikProps: FormikProps<MacroAdjustmentWizardFormikValues>;
  headersIndent: string;
  activeStep: number;
  setActiveStep: Function;
  adjustmentCombination: MacroAdjustmentCombinations | undefined;
  steps: (
    formikProps: FormikProps<MacroAdjustmentWizardFormikValues>,
  ) => MacroAdjustmentStep[];
  scenarioId: number;
  isSavingAdjustment: boolean;
};

const MacroAdjustmentsWizardHeaderAndStepper = ({
  handleNavigateToAdjustmentsPage,
  formikProps,
  headersIndent,
  activeStep,
  setActiveStep,
  adjustmentCombination,
  scenarioId,
  steps,
  isSavingAdjustment,
}: MacroAdjustmentsWizardHeaderAndStepperProps) => {
  const { t } = useTranslation("macro");
  const { palette } = useTheme();
  const { values } = formikProps;

  const { data: adjustments } =
    useGetAllScenariosMacroScenarioIdAdjustmentsGet(scenarioId);

  const isConflictingEfAdjustment = useMemo<boolean>(() => {
    if (
      adjustments &&
      values.selectedPillar &&
      values.selectedInputArea &&
      values.payload.filters.countries[0]
    ) {
      return CheckConflictingEfAdjustment(
        adjustmentCombination as MacroAdjustmentCombinations,
        adjustments,
        values.selectedPillar,
        values.selectedInputArea,
        values.payload.filters.countries[0] as string,
        values.id,
      );
    } else {
      return false;
    }
  }, [
    adjustmentCombination,
    adjustments,
    values.id,
    values.payload.filters.countries,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  const isConflictingAdjustment = useMemo<boolean>(() => {
    if (adjustments && values.selectedPillar && values.selectedInputArea) {
      return CheckConflictingAdjustment(
        adjustmentCombination as MacroAdjustmentCombinations,
        adjustments,
        values.selectedPillar,
        values.selectedInputArea,
        values.id,
      );
    } else {
      return false;
    }
  }, [
    adjustmentCombination,
    adjustments,
    values.id,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  const handleStepClick = useCallback(
    (index: number) => {
      const canProceedToStep2 =
        adjustmentCombination &&
        values.selectedPillar &&
        values.selectedInputArea &&
        values.selectedAdjustmentType &&
        values.payload.name?.length >= 3;

      const canProceedToStep3 =
        (([
          MacroAdjustmentCombinations.SalesMix_CategoryMix,
          MacroAdjustmentCombinations.SalesMix_DLZShare,
          MacroAdjustmentCombinations.SalesMix_PackMix,
          MacroAdjustmentCombinations.SalesMix_SalesGrowth,
          MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors,
        ].includes(adjustmentCombination as MacroAdjustmentCombinations) &&
          values.payload.entities.length) ||
          values.selectedEntities.length) &&
        ((adjustmentCombination ===
          MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment &&
          values.payload.filters.brands?.length) ||
          ([
            MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment,
            MacroAdjustmentCombinations.Ingredients_IngredientsEmissionFactors,
            MacroAdjustmentCombinations.Logistics_LogisticsOtherVehicleMixAndEmissionFactors,
            MacroAdjustmentCombinations.Logistics_LogisticsRoadVehicleMixAndEmissionFactors,
            MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors,
            MacroAdjustmentCombinations.Manufacturing_ManufacturingSourceMixAndEmissionFactors,
            MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors,
            MacroAdjustmentCombinations.CDE_CDEFleetBreakdown,
            MacroAdjustmentCombinations.SalesMix_CategoryMix,
            MacroAdjustmentCombinations.SalesMix_DLZShare,
            MacroAdjustmentCombinations.SalesMix_PackMix,
            MacroAdjustmentCombinations.SalesMix_SalesGrowth,
          ].includes(adjustmentCombination as MacroAdjustmentCombinations) &&
            values.payload.filters.countries?.length) ||
          ([
            MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment,
            MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment,
            MacroAdjustmentCombinations.Packaging_RecycledContent,
            MacroAdjustmentCombinations.Packaging_NetLossRate,
            MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors,
            MacroAdjustmentCombinations.Packaging_RecoveryRate,
            MacroAdjustmentCombinations.Manufacturing_ManufacturingEur,
            MacroAdjustmentCombinations.Logistics_DistanceTravelled,
          ].includes(adjustmentCombination as MacroAdjustmentCombinations) &&
            values.selectedProducts?.length));
      if (
        (index === 1 && canProceedToStep2 && !isConflictingAdjustment) ||
        (index === 2 && canProceedToStep3 && !isConflictingEfAdjustment) ||
        index === 0
      ) {
        setActiveStep(index);
      }
    },
    [
      adjustmentCombination,
      isConflictingAdjustment,
      isConflictingEfAdjustment,
      setActiveStep,
      values.payload.entities.length,
      values.payload.filters.brands?.length,
      values.payload.filters.countries?.length,
      values.payload.name?.length,
      values.selectedAdjustmentType,
      values.selectedEntities.length,
      values.selectedInputArea,
      values.selectedPillar,
      values.selectedProducts?.length,
    ],
  );

  return (
    <Box>
      <Box tabIndex={0}>
        <Button
          variant="text"
          onClick={() => handleNavigateToAdjustmentsPage(formikProps)}
          disabled={isSavingAdjustment}
          startIcon={
            <CustomIcon
              name={Icons.CHEVRON_LEFT}
              width={24}
              height={24}
              fill={
                isSavingAdjustment ? palette.grey[400] : palette.primary.main
              }
            />
          }
          sx={{ padding: 0, py: 1 }}
        >
          {t("macro:adjustmentsPage.wizard.backToAdjustmentsPage")}
        </Button>
      </Box>
      <Box
        display={"flex"}
        alignItems={"center"}
        justify-content={"center"}
        mb={2}
      >
        {steps(formikProps)[activeStep].stepTitle && (
          <Box tabIndex={0}>
            <Typography
              className="text-ellipsis"
              variant={"h1"}
              sx={{
                paddingX: headersIndent,
                transition: "padding 0.3s ease-in-out",
              }}
            >
              {steps(formikProps)[activeStep].stepTitle}
            </Typography>
          </Box>
        )}

        {steps(formikProps)[activeStep].stepSubtitle && (
          <Box display={"flex"} alignItems={"center"}>
            <Box>
              <FiberManualRecordIcon
                sx={{
                  mx: 1,
                  fontSize: "5px",
                  color: "grey",
                }}
              />
            </Box>
            <Box>
              <Typography
                className="text-ellipsis"
                mt="2px"
                variant={"h2"}
                sx={{
                  paddingX: headersIndent,
                  transition: "padding 0.3s ease-in-out",
                }}
              >
                {steps(formikProps)[activeStep].stepSubtitle}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box mb={4}>
        <Stepper activeStep={activeStep}>
          {Array.isArray(steps(formikProps)) &&
            steps(formikProps).map((step, i) => (
              <Step sx={{ pl: i === 0 ? 0 : "8px" }} key={step.index}>
                <StepLabel
                  sx={{
                    cursor: "pointer !important",
                    ".Mui-disabled": {
                      opacity: 0.6,
                      ".MuiStepIcon-root": {
                        color: "#fff",
                        border: "1px solid #979797",
                        borderRadius: "50%",
                        ".MuiStepIcon-text": {
                          color: "#979797",
                          fill: "#979797",
                        },
                      },
                    },
                  }}
                  onClick={() => handleStepClick(step.index)}
                  tabIndex={activeStep === step.index ? 0 : -1}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
        </Stepper>
      </Box>
    </Box>
  );
};

export default MacroAdjustmentsWizardHeaderAndStepper;
